/**
 * Ship date color palette — 6 distinct colors cycling by sorted-date index.
 * Dates are assigned a color slot based on their position in a sorted unique-date list.
 */

export interface ShipDateColorSlot {
  /** Tailwind classes for the banner background */
  bannerBg: string;
  /** Tailwind classes for the card border */
  cardBorder: string;
  /** Hex badge background for plain <span> usage */
  badgeBg: string;
  /** Text colour for the banner */
  text: string;
}

const PALETTE: ShipDateColorSlot[] = [
  { bannerBg: 'bg-amber-500 dark:bg-amber-600',  cardBorder: 'border-amber-400 dark:border-amber-600',  badgeBg: '#f59e0b', text: 'text-white' },
  { bannerBg: 'bg-teal-500 dark:bg-teal-600',    cardBorder: 'border-teal-400 dark:border-teal-600',    badgeBg: '#14b8a6', text: 'text-white' },
  { bannerBg: 'bg-blue-500 dark:bg-blue-600',    cardBorder: 'border-blue-400 dark:border-blue-600',    badgeBg: '#3b82f6', text: 'text-white' },
  { bannerBg: 'bg-purple-500 dark:bg-purple-600',cardBorder: 'border-purple-400 dark:border-purple-600',badgeBg: '#a855f7', text: 'text-white' },
  { bannerBg: 'bg-rose-500 dark:bg-rose-600',    cardBorder: 'border-rose-400 dark:border-rose-600',    badgeBg: '#f43f5e', text: 'text-white' },
  { bannerBg: 'bg-green-600 dark:bg-green-700',  cardBorder: 'border-green-500 dark:border-green-700',  badgeBg: '#16a34a', text: 'text-white' },
];

/**
 * Build a map of ISO-date-string → color slot.
 * Pass in the full list of unique date strings present in the current view
 * (nulls are ignored).  Dates are sorted chronologically so earlier arrivals
 * always get the same colour within a session.
 */
export function buildShipDateColorMap(dates: (string | null | undefined)[]): Map<string, ShipDateColorSlot> {
  const uniqueSorted = [...new Set(dates.filter(Boolean) as string[])]
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  const map = new Map<string, ShipDateColorSlot>();
  uniqueSorted.forEach((date, idx) => {
    map.set(date, PALETTE[idx % PALETTE.length]);
  });
  return map;
}

/** Convenience: look up the color slot for a single date, with a fallback. */
export function getShipDateColor(
  date: string | null | undefined,
  colorMap: Map<string, ShipDateColorSlot>,
): ShipDateColorSlot {
  if (!date) return PALETTE[0];
  return colorMap.get(date) ?? PALETTE[0];
}
